import React, { useState, useEffect } from "react";
import { Drawer, Typography, Space, Button, Row, Col, Form, Divider, notification } from "antd";
import dayjs from "../../utils/day";
import metaData from "../../data/metaData";
import { showErrMsg } from "../../utils/error";
import log from "../../utils/log";
import c from "../../utils/common";
import { getPeriodStatus } from "../../utils/estamp";
import useCompanyStore from "../../states/useCompany";

//
import PeriodPicker from "../General/PeriodPicker";
import Number from "../General/Number";
import DatePicker from "../../components/General/DatePicker";
import Name from "../General/Name";

const { Title, Text } = Typography;

const PeriodDetailDrawer = ({
  open,
  period,
  periods = [],
  gifts = [],
  header,
  closable = true,
  isLoading = false,
  canDelete = false,
  confirmBtnText,
  onClose,
  onDelete,
  onConfirm,
}) => {
  const { getMaxShopExpiry } = useCompanyStore();
  const [form] = Form.useForm();
  const [localPeriod, setLocalPeriod] = useState(period);

  const validateForm = async (form) => {
    try {
      await form.validateFields();
    } catch (error) {
      log.error(error);
      c.message({
        type: "error",
        content: "Some of the input fields are invalid. Please review and save again",
      });
      throw new Error("Some of the input fields are invalid. Please review and save again");
    }
  };

  const checkTimeValidity = (time) => {
    if (time?.expiry && time?.earn?.end) {
      const expiry = dayjs(time.expiry);
      const earnEnd = dayjs(time.earn.end);

      if (!expiry.isAfter(earnEnd)) {
        throw new Error("The expiry date must be after earning end date");
      }
    }
  };

  const submit = async () => {
    try {
      // Validate the form
      await validateForm(form);

      const { time } = localPeriod || {};
      checkTimeValidity(time);

      onConfirm(localPeriod);
    } catch (error) {
      showErrMsg(error);
    }
  };

  // Update local state when period prop changes
  useEffect(() => {
    if (period) {
      setLocalPeriod(period);
    }
  }, [period]);

  // Function to update local gift state
  const updateLocalPeriodData = (updatedData) => {
    log.debug(updatedData);

    setLocalPeriod((prev) => ({
      ...prev,
      ...updatedData,
    }));
  };

  return (
    <Drawer
      title={header}
      open={open}
      width="50%"
      onClose={() => {
        setLocalPeriod();
        onClose();
      }}
      closable={closable}
      extra={
        <Space>
          {period?.id && canDelete ? (
            <Button
              color="danger"
              variant="outlined"
              size="large"
              loading={isLoading}
              style={{ marginRight: "1rem" }}
              onClick={() => onDelete(period.id)}
            >
              Delete
            </Button>
          ) : null}
          <Button type="primary" size="large" loading={isLoading} onClick={submit}>
            {confirmBtnText}
          </Button>
        </Space>
      }
    >
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        {period?.id ? (
          <Button
            type="text"
            style={{
              fontSize: "1rem",
              color: `var(--ion-color-${
                ["ongoing", "earnEnded"].includes(getPeriodStatus(period)) ? "green-600" : "orange-600"
              })`,
              background: `var(--ion-color-${
                ["ongoing", "earnEnded"].includes(getPeriodStatus(period)) ? "green-100" : "orange-100"
              })`,
            }}
          >
            {getPeriodStatus(period) === "pending" ? "pending" : "ongoing"}
          </Button>
        ) : null}

        <InfoItem
          label="Name"
          value={
            <Name
              state={{
                data: localPeriod?.name,
                form: form,
                rules: [{ required: true, message: "required" }],
                onChange: updateLocalPeriodData,
              }}
              readOnly={false}
              maxLength={60}
              showCount
            />
          }
        />

        <Text strong>Coin Rules</Text>
        <Space align="center">
          <Text>Earn 1 Coin for every $</Text>
          <Number
            state={{
              data: localPeriod?.rate,
              form: form,
              rules: [
                { required: true, message: "required" },
                {
                  validator: (_, value) =>
                    value > 0 ? Promise.resolve() : Promise.reject(new Error("Must be greater than zero")),
                },
              ],
              onChange: (value) => value > 0 && updateLocalPeriodData({ rate: value }),
            }}
            min={1}
            keyName="rate"
            readOnly={false}
            style={{ width: "7.5rem" }}
          />
          <Text> spent on vouchers in a single purchase.</Text>
        </Space>

        <Divider />

        <InfoItem
          label="Coin Earning Period"
          value={
            <PeriodPicker
              state={{
                data: localPeriod?.time?.earn,
                form: form,
                rules: [{ required: true, message: "required" }],
                onChange: (updated) => {
                  updateLocalPeriodData({
                    time: { ...localPeriod.time, earn: updated },
                  });
                },
                keyName: "earnPeriod",
              }}
              minDate={dayjs()}
              setWarningMessage={(message) => {
                if (message) {
                  notification.warning({
                    description: <Text strong>{message}</Text>,
                  });
                }
              }}
              occupiedDates={[
                ...(periods
                  ?.filter((period) => period.id !== localPeriod?.id) // Exclude current period from occupied dates
                  .map((period) => ({
                    start: period.time.earn.start,
                    end: period.time.earn.end,
                  })) || []),
              ]}
              maxDate={getMaxShopExpiry()} // Set maxDate to disable this date and days after it
            />
          }
        />

        <InfoItem
          label="Coin Expiry"
          description="must be after earning period"
          value={
            <DatePicker
              state={{
                data: localPeriod?.time?.expiry,
                form: form,
                rules: [{ required: true, message: "required" }],
                onChange: (updated) => {
                  updateLocalPeriodData({
                    time: { ...localPeriod.time, expiry: updated },
                  });
                },
                keyName: "expiry",
              }}
              minDate={
                localPeriod?.time?.earn?.end ? dayjs(localPeriod.time.earn.end).add(1, "day") : dayjs().add(1, "day")
              }
              maxDate={getMaxShopExpiry()}
            />
          }
        />

        {/* {localPeriod?.time?.expiry ? (
          <>
            <SectionHeader
              title="Gifts Preview"
              description="The following gifts are available before the selected expiry date."
            />
            <GiftTable
              state={{
                // Filter gifts where redeem end time is before the expiry time
                data: gifts.filter((gift) => !moment(gift.time.redeem.end).isAfter(localPeriod.time.expiry)),
              }}
              readOnly
            />
            <Row style={{ height: "5rem" }}></Row>
          </>
        ) : null} */}

        {/* <img
          style={{
            width: 350,
            objectFit: "cover",
            borderRadius: "0.5rem",
            border: "1px solid var(--ion-color-step-200)",
          }}
          src={
            metaData.blobPreURL + "clientUpload-" + gifts?.[0]?.product?.gallery?.[0] + ".jpeg" + metaData.blobPostURL
          }
        />
        <SectionHeader
          title={gifts?.[0]?.product?.name}
          description={`price $ ${gifts?.[0]?.product?.price}`}
          style={{ margin: 0 }}
        /> */}
      </Space>
    </Drawer>
  );
};

const InfoItem = ({ label, description, value, icon }) => (
  <Row justify="start" style={{ marginBottom: "1rem" }}>
    <Col span={8}>
      <Space style={{ width: "100%" }}>
        {icon}
        <Text strong>{label}:</Text>
      </Space>
      <Text type="secondary">{description}</Text>
    </Col>
    <Col span={16}>
      <Text strong>{value}</Text>
    </Col>
  </Row>
);

export default PeriodDetailDrawer;
